<template>
  <client-only>
    <TransitionRoot :show="isOpen" as="template">
      <Dialog
        component-name="modal-bath-concept-filters"
        data-skin="white"
        class="relative z-50"
        data-lenis-prevent
      >
        <TransitionChild
          enter="transition-opacity duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <!-- The backdrop, rendered as a fixed sibling to the panel container -->
          <div
            class="modal-backdrop"
            aria-hidden="true"
            @click="$emit('close')"
          ></div>
        </TransitionChild>

        <!-- Full-screen scrollable container -->
        <div
          class="pointer-events-none overflow-clip fixed inset-0 flex justify-start w-full h-full"
        >
          <!-- Container to center the panel -->
          <div
            class="flex justify-start items-stretch w-full h-full max-h-full"
            :style="`--modal-bath-concept-filters-x-leave: ${
              isFiltered ? '0' : '-100%'
            }; --modal-bath-concept-filters-y-leave: ${
              isFiltered ? '100%' : '0'
            };`"
          >
            <TransitionChild
              enter="transition-transform duration-300 ease-out delay-200"
              enter-from="-translate-x-full"
              enter-to="translate-x-0"
              leave="transition-transform duration-300 ease-in"
              leave-from="translate-y-0 translate-x-0"
              leave-to="translate-y-[--modal-bath-concept-filters-y-leave] translate-x-[--modal-bath-concept-filters-x-leave]"
            >
              <!-- The actual dialog panel -->
              <component
                :is="$device.isMobile ? 'div' : DialogPanel"
                data-skin="white"
                class="relatice top-0 left-0 w-screen h-screen overflow-clip"
              >
                <!-- RESULTS / FACETS -->
                <transition
                  mode="out-in"
                  enter-active-class="transition-transform duration-400 ease-in-out "
                  enter-from-class="translate-y-full"
                  enter-to-class="translate-y-0"
                  leave-active-class="transition-transform duration-300 ease-out"
                  leave-from-class="translate-x-0"
                  leave-to-class="-translate-x-full"
                >
                  <section
                    v-if="isFiltered"
                    key="results"
                    class="isolation relative-top-0 left-0 z-2 pointer-events-auto w-full h-screen pb-[calc(100lvh-100svh)] !m-0 !p-0 [background:radial-gradient(var(--color-fill-base),var(--color-fill-muted))]"
                  >
                    <div
                      class="w-full h-full max-h-full flex flex-col justify-evenly gap-3xl text-center text-skin-base py-edge"
                    >
                      <header class="flex flex-col items-center">
                        <div
                          class="text-skin-muted text-overline-small/none"
                          v-if="filteredResults.current.label"
                          v-html="filteredResults.current.label"
                        ></div>

                        <h1
                          v-if="filteredResults.current.value"
                          class="text-heading-5"
                          v-html="filteredResults.current.value"
                        ></h1>
                      </header>

                      <div class="flex justify-center items-center min-h-min">
                        <carousel-bath-concept-launcher
                          v-if="filteredResults.list?.length"
                          :projects="filteredResults.list"
                        />
                      </div>

                      <footer class="flex justify-center">
                        <btn-pill
                          :filled="true"
                          :title="'Cancella filtro'"
                          size="big"
                          icon="close"
                          @click="clearFilters"
                        />
                      </footer>
                    </div>
                  </section>

                  <section
                    v-else
                    key="filters"
                    class="relative top-0 left-0 z-2 pointer-events-auto overflow-y-auto flex flex-col gap-2xl w-[calc(100vw-80px)] max-w-[600px] extdesktop:max-w-[700px] h-full max-h-full bg-skin-base p-edge"
                  >
                    <h1
                      class="text-heading-4"
                      v-html="$t.labels.filter_by"
                    ></h1>

                    <form-filters
                      v-if="facets"
                      ref="tpl_filters"
                      :use-search="false"
                      :use-padding-top="false"
                      :use-sticky-header="false"
                      :use-indoor-outdoor="false"
                      :use-active-filters="false"
                      :facets="facets"
                      :total="total"
                      :disabled="isFiltered || isLoading"
                      class="w-full"
                    />
                  </section>
                </transition>
                <!-- end RESULTS / FACETS -->
              </component>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </client-only>
</template>

<script setup>
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
} from "@headlessui/vue";

const props = defineProps({
  isOpen: {
    type: [Boolean, String],
    required: false,
  },
});

const route = useRoute();
const emit = defineEmits(["close", "require-open"]);
const isFiltered = ref(false);
const isLoading = ref(false);
const filteredResults = ref();
const originalData = ref();
const filtersData = ref();
const total = ref(0);
const facets = ref("");
const router = useRouter();

const loadItems = async (query) => {
  const queryToString = (query) => {
    return Object.keys(query)
      .map((key) => `${key}=${query[key]}`)
      .join("&");
  };

  return $fetch(
    `/api/filter_bathconcept_projects${
      query ? `?${queryToString({ lng: route.params.locale, ...query })}` : ""
    }`
  ).then((res) => {
    return res;
  });
};

const updateFilteredResults = () => {
  if (!route.query || Object.keys(route.query).length === 0) {
    return;
  }

  let current,
    facets = filtersData.value.facets;

  for (let i = 0; i < facets.length; i++) {
    for (let j = 0; j < facets[i].items.length; j++) {
      const value = Object.values(route.query)[0];
      if (facets[i].items[j].value == value) {
        current = {
          label: facets[i].title,
          value: facets[i].items[j].label,
        };
        break;
      }
    }
  }

  filteredResults.value = {
    facet: filtersData.value.facet,
    list: filtersData.value.results,
    current,
  };
};

const data = await loadItems(route.query);

originalData.value = JSON.parse(JSON.stringify(data));
filtersData.value = { ...data };
facets.value = JSON.stringify(filtersData.value.facets) || "";
total.value = parseInt(data.total) || 0;

if (data.results.length) {
  updateFilteredResults();
  isFiltered.value = true;
  emit("require-open");
}

function clearFilters() {
  facets.value = "";
  total.value = 0;
  router.push({ query: {} });

  emit("close");
}

watch(
  () => route.query,
  (value) => {
    // if value is an empty object, return
    if (!value || Object.keys(value).length === 0) {
      return;
    }

    isLoading.value = true;
    loadItems(value).then((data) => {
      filtersData.value = { ...data };
      facets.value = JSON.stringify(data.facets) || "";
      total.value = parseInt(data.total) || 0;

      if (data.results.length) {
        updateFilteredResults();
        isFiltered.value = true;
        isLoading.value = false;
      }
    });
  }
);

watch(
  () => props.isOpen,
  (value) => {
    if (value) {
      if (route.query && Object.keys(route.query).length > 0) {
        return;
      }

      isFiltered.value = false;
      filteredResults.value = null;
      filtersData.value = { ...originalData.value };
      facets.value = JSON.stringify(filtersData.value.facets) || "";
      total.value = parseInt(originalData.total) || 0;
    }
  }
);

watch(
  () => route.name,
  () => {
    emit("close");
  }
);
</script>
